import {
  LE_V2_STUDY_DELIVERY_CONTRACT_VERSION,
  type StudyDeliveryV2,
} from '../learningEngineV2/studyDeliveryContract';

export type AssignmentEngineRpcErrorLike = {
  code?: string;
  message: string;
  details?: string;
  hint?: string;
};

export type AssignmentEngineRpcClient = {
  rpc: (
    functionName: string,
    args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: AssignmentEngineRpcErrorLike | null }>;
};

export class AssignmentEngineRpcError extends Error {
  readonly code?: string;
  readonly details?: string;
  readonly hint?: string;

  constructor(operation: string, error: AssignmentEngineRpcErrorLike) {
    super(`${operation}: ${error.message}`);
    this.name = 'AssignmentEngineRpcError';
    this.code = error.code;
    this.details = error.details;
    this.hint = error.hint;
  }
}

function assertWorksheetBoundStudyDelivery(value: unknown): StudyDeliveryV2 {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('create_today_math_assignment_v2: server returned a non-object payload.');
  }
  const result = value as Record<string, unknown>;
  if (result.schemaVersion !== 2 || result.contractVersion !== LE_V2_STUDY_DELIVERY_CONTRACT_VERSION) {
    throw new Error('create_today_math_assignment_v2: incompatible Study Delivery contract.');
  }
  const assignment = result.assignment;
  if (!assignment || typeof assignment !== 'object' || Array.isArray(assignment)) {
    throw new Error('create_today_math_assignment_v2: assignment payload missing.');
  }
  const worksheetVersionId = (assignment as Record<string, unknown>).worksheetVersionId;
  if (typeof worksheetVersionId !== 'string' || worksheetVersionId.length < 8) {
    throw new Error('create_today_math_assignment_v2: assignment is not worksheet-bound.');
  }
  if (!Array.isArray(result.questions) || result.questions.length === 0) {
    throw new Error('create_today_math_assignment_v2: delivery contains no questions.');
  }
  return value as StudyDeliveryV2;
}

export async function createTodayMathAssignmentV2(
  client: AssignmentEngineRpcClient,
  input: { learningDate?: string | null } = {},
): Promise<StudyDeliveryV2> {
  const { data, error } = await client.rpc('create_today_math_assignment_v2', {
    p_learning_date: input.learningDate ?? null,
  });
  if (error) throw new AssignmentEngineRpcError('create_today_math_assignment_v2', error);
  return assertWorksheetBoundStudyDelivery(data);
}
